import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { FiPlus } from 'react-icons/fi'
import { ROUTES } from '@/constants/routes'
import {
  getOperationalCosts,
  createOperationalCost,
  deleteOperationalCost,
  importSupplierOperationalCosts,
  getCategoriesForStage,
  type OperationalCost,
  type OperationalCostStage,
  type CreateOperationalCostPayload,
} from '@/services/api/operationalCosts'
import { formatCurrency } from '@/utils/currencyFormat'
import PageSizeSelect from '@/components/common/PageSizeSelect'

const STAGE_LABELS: Record<OperationalCostStage, string> = {
  clearing: 'Clearing',
  storage_dispatch: 'Storage & Dispatch',
  order_fulfillment: 'Order Fulfillment (Exceptions)',
}

const FinanceOperationalCosts = () => {
  const location = useLocation()
  const [costs, setCosts] = useState<OperationalCost[]>([])
  const [meta, setMeta] = useState<{ total: number; page: number; limit: number; totalPages: number } | null>(null)
  const [loading, setLoading] = useState(true)
  const [stageFilter, setStageFilter] = useState<OperationalCostStage | ''>('')
  const [page, setPage] = useState(1)
  const [resultsPerPage, setResultsPerPage] = useState(25)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [addStage, setAddStage] = useState<OperationalCostStage>('clearing')
  const [addCategory, setAddCategory] = useState('')
  const [addDescription, setAddDescription] = useState('')
  const [addAmount, setAddAmount] = useState('')
  const [addCurrency, setAddCurrency] = useState('ZAR')
  const [addClearingRecordId, setAddClearingRecordId] = useState('')
  const [addLoadOutId, setAddLoadOutId] = useState('')
  const [addOrderFulfillmentId, setAddOrderFulfillmentId] = useState('')
  const [addShipmentId, setAddShipmentId] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [importing, setImporting] = useState(false)
  const [importMessage, setImportMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const fetchCosts = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getOperationalCosts({
        page,
        limit: resultsPerPage,
        ...(stageFilter ? { stage: stageFilter as OperationalCostStage } : {}),
      })
      setCosts(res.operationalCosts ?? [])
      setMeta(res.meta ?? null)
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Failed to load operational costs')
      setCosts([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCosts()
  }, [page, stageFilter, resultsPerPage])

  const categoriesForStage = getCategoriesForStage(addStage)

  const tabs = [
    { label: 'Overview', path: ROUTES.FINANCE_OVERVIEW },
    { label: 'Invoices', path: ROUTES.FINANCE_INVOICES },
    { label: 'Purchases', path: ROUTES.FINANCE_PURCHASES },
    { label: 'Products and Services', path: ROUTES.FINANCE_PRODUCTS_SERVICES },
    { label: 'Operational Costs', path: ROUTES.FINANCE_OPERATIONAL_COSTS },
    { label: 'Business Snapshot', path: ROUTES.FINANCE_SNAPSHOT },
    { label: 'Business Analytics', path: ROUTES.FINANCE_ANALYTICS },
  ]

  const handleAddCost = async (e: React.FormEvent) => {
    e.preventDefault()
    const amount = parseFloat(addAmount)
    if (isNaN(amount) || amount <= 0) {
      setError('Enter a valid amount')
      return
    }
    if (!addCategory.trim()) {
      setError('Select or enter a category')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const payload: CreateOperationalCostPayload = {
        stage: addStage,
        category: addCategory.trim(),
        amount,
        currency: addCurrency || 'ZAR',
        ...(addDescription.trim() ? { description: addDescription.trim() } : {}),
        ...(addClearingRecordId.trim() ? { clearingRecordId: addClearingRecordId.trim() } : {}),
        ...(addLoadOutId.trim() ? { loadOutId: addLoadOutId.trim() } : {}),
        ...(addOrderFulfillmentId.trim() ? { orderFulfillmentId: addOrderFulfillmentId.trim() } : {}),
        ...(addShipmentId.trim() ? { shipmentId: addShipmentId.trim() } : {}),
      }
      await createOperationalCost(payload)
      setIsAddModalOpen(false)
      setAddAmount('')
      setAddDescription('')
      setAddCategory('')
      setAddClearingRecordId('')
      setAddLoadOutId('')
      setAddOrderFulfillmentId('')
      setAddShipmentId('')
      fetchCosts()
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Failed to create cost')
    } finally {
      setSubmitting(false)
    }
  }

  const handleImportFile = async (file: File) => {
    setImporting(true)
    setError(null)
    setImportMessage(null)
    try {
      const result = await importSupplierOperationalCosts(file)
      setImportMessage(
        `Imported ${result.inserted} new costs, updated ${result.updated}. ${result.linkedToOrders} linked to an order, ${result.unlinked} awaiting a BL / booking / lot match.`
      )
      fetchCosts()
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Failed to import supplier sheet')
    } finally {
      setImporting(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this operational cost?')) return
    try {
      await deleteOperationalCost(id)
      fetchCosts()
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Failed to delete')
    }
  }

  return (
    <div className="space-y-6" style={{ maxWidth: '100%' }}>
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
          <h1
            className="text-lg sm:text-xl"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontSize: 'clamp(18px, 4vw, 20px)',
              lineHeight: '28px',
              color: '#212126',
            }}
          >
            Operational Costs
          </h1>
          <span
            className="text-xs sm:text-sm"
            style={{ fontFamily: 'Lexend Deca', color: '#69697A' }}
          >
            <span style={{ color: '#69697A' }}>Home</span>
            <span style={{ margin: '0 4px' }}> &gt; </span>
            <span style={{ color: '#212126' }}>Operational Costs</span>
          </span>
        </div>
        <p className="text-sm mb-4" style={{ fontFamily: 'Lexend Deca', color: '#69697A' }}>
          Import Marc's SUPPLIERS book from 1 April. Clearing, warehouse and truck invoices attach to the matching order by BL, booking or lot.
        </p>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div
            className="flex items-center overflow-x-auto scrollbar-hide flex-1 min-w-0 gap-0"
            style={{
              width: '100%',
              minWidth: 0,
              height: '38px',
              borderRadius: '8px',
              border: '1px solid #E9E9EA',
              backgroundColor: '#FFFFFF',
              padding: '2px',
            }}
          >
            {tabs.map((tab) => {
              const isActive = location.pathname === tab.path
              return (
                <Link
                  key={tab.path}
                  to={tab.path}
                  className="flex-grow flex items-center justify-center px-2 py-1.5 rounded text-sm font-medium transition-colors whitespace-nowrap"
                  style={{
                    fontFamily: 'Lexend Deca',
                    backgroundColor: isActive ? '#212126' : 'transparent',
                    color: isActive ? '#FFFFFF' : '#69697A',
                  }}
                >
                  {tab.label}
                </Link>
              )
            })}
          </div>

          <div className="flex items-center gap-2 " >
            <select
              value={stageFilter}
              onChange={(e) => {
                setStageFilter(e.target.value as OperationalCostStage | '')
                setPage(1)
              }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              style={{ fontFamily: 'Lexend Deca' }}
            >
              <option value="">All stages</option>
              {(Object.keys(STAGE_LABELS) as OperationalCostStage[]).map((s) => (
                <option key={s} value={s}>{STAGE_LABELS[s]}</option>
              ))}
            </select>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) void handleImportFile(file)
              }}
            />
            <button
              type="button"
              disabled={importing}
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              style={{ fontFamily: 'Lexend Deca', color: '#212126' }}
            >
              {importing ? 'Importing…' : 'Import suppliers'}
            </button>
            <button
              type="button"
              onClick={() => setIsAddModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-lg hover:opacity-90"
              style={{ fontFamily: 'Lexend Deca', backgroundColor: '#212126' }}
            >
              <FiPlus className="w-4 h-4" /> Add Cost
            </button>
          </div>
        </div>
      </div>

      {importMessage && (
        <div className="p-3 rounded-lg bg-green-50 text-green-800 text-sm" style={{ fontFamily: 'Lexend Deca' }}>
          {importMessage}
        </div>
      )}
      {error && (
        <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm" style={{ fontFamily: 'Lexend Deca' }}>
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500" style={{ fontFamily: 'Lexend Deca' }}>
            Loading…
          </div>
        ) : costs.length === 0 ? (
          <div className="p-8 text-center text-gray-500" style={{ fontFamily: 'Lexend Deca' }}>
            No operational costs yet. Import the SUPPLIERS Excel from 1 April, or add a single cost.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200" style={{ fontFamily: 'Lexend Deca' }}>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Linked to</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Xero</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {costs.map((c) => (
                  <tr key={c.id}>
                    <td className="px-4 py-3 text-sm text-gray-900">{STAGE_LABELS[c.stage]}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{c.category}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{c.description || '—'}</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">
                      {formatCurrency(Number(c.amount), c.currency)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {c.orderFulfillment?.salesforceOrder?.orderNumber ||
                        c.orderFulfillment?.salesforceOrder?.xeroInvoiceId ||
                        c.orderFulfillment?.containerNumber ||
                        c.shipment?.containerNumber ||
                        c.clearingRecord?.lotNumber ||
                        c.loadOut?.lotNumber ||
                        'Awaiting order link'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {c.xeroInvoiceId ? (
                        <span className="text-green-600">Synced</span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => handleDelete(c.id)}
                        className="text-red-600 hover:underline text-xs"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {meta && costs.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Page {meta.page} of {meta.totalPages} ({meta.total} total)
            </span>
            <div className="flex items-center gap-2">
            <PageSizeSelect
              value={resultsPerPage}
              onChange={(size) => {
                setResultsPerPage(size)
                setPage(1)
              }}
            />
              <button
                type="button"
                disabled={meta.page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={meta.page >= meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Cost Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setIsAddModalOpen(false)}>
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
            style={{ fontFamily: 'Lexend Deca' }}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Operational Cost</h3>
            <form onSubmit={handleAddCost} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
                <select
                  value={addStage}
                  onChange={(e) => {
                    setAddStage(e.target.value as OperationalCostStage)
                    setAddCategory('')
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                >
                  {(Object.keys(STAGE_LABELS) as OperationalCostStage[]).map((s) => (
                    <option key={s} value={s}>{STAGE_LABELS[s]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={addCategory}
                  onChange={(e) => setAddCategory(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                >
                  <option value="">Select category</option>
                  {categoriesForStage.map((cat) => (
                    <option key={cat} value={cat}>{cat.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={addAmount}
                    onChange={(e) => setAddAmount(e.target.value)}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="0.00"
                    required
                  />
                  <select
                    value={addCurrency}
                    onChange={(e) => setAddCurrency(e.target.value)}
                    className="w-20 border border-gray-300 rounded-lg px-2 py-2"
                  >
                    <option value="ZAR">ZAR</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <input
                  type="text"
                  value={addDescription}
                  onChange={(e) => setAddDescription(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="e.g. Customs duties for container XYZ"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Link to (optional)</label>
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <input
                    type="text"
                    value={addShipmentId}
                    onChange={(e) => setAddShipmentId(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1.5"
                    placeholder="Shipment ID"
                  />
                  <input
                    type="text"
                    value={addClearingRecordId}
                    onChange={(e) => setAddClearingRecordId(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1.5"
                    placeholder="Clearing record ID"
                  />
                  <input
                    type="text"
                    value={addLoadOutId}
                    onChange={(e) => setAddLoadOutId(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1.5"
                    placeholder="Load out ID"
                  />
                  <input
                    type="text"
                    value={addOrderFulfillmentId}
                    onChange={(e) => setAddOrderFulfillmentId(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1.5"
                    placeholder="Order fulfillment ID"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                >
                  {submitting ? 'Saving…' : 'Add Cost'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default FinanceOperationalCosts
